import { useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon } from '@lexdraft/ui';
import { pathMatches, visibleNavGroups } from './nav-config';
import { useAuthStore } from '@/store/auth';
import { useUIStore } from '@/store/ui';
import { useNavHighlighter } from '@/hooks/useNavHighlighter';
import { useMyUsage } from '@/hooks/useMyUsage';
import { useMeFeatures } from '@/hooks/useFirmAdmin';
import { useTeamChatStream, useTeamChatUnreadTotal } from '@/hooks/useTeamChat';
import { LogoMark, Wordmark } from '@/components/brand/Logo';
import { AccountMenu } from './AccountMenu';

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const initials = (user?.name || 'AS').split(' ').map((s) => s[0]).slice(0, 2).join('');
  const navRef = useRef<HTMLElement>(null);
  const hl = useNavHighlighter(navRef);
  const usage = useMyUsage();
  const meFeatures = useMeFeatures();
  const navGroups = useMemo(
    () => visibleNavGroups(meFeatures.data?.features ?? []),
    [meFeatures.data],
  );
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  // The Sidebar is mounted once for the whole app shell, so it owns the
  // team-chat live stream as well as rendering its unread badge.
  useTeamChatStream();
  const chatUnread = useTeamChatUnreadTotal();

  // Close the drawer on route change (mobile UX).
  useEffect(() => {
    if (sidebarOpen) toggleSidebar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Keep the current page's item in view when the menu is taller than the screen.
  useEffect(() => {
    const t = window.setTimeout(() => {
      navRef.current?.querySelector<HTMLElement>('.nav-item.active')?.scrollIntoView({ block: 'nearest' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [location.pathname, navGroups]);

  // Close on Esc.
  useEffect(() => {
    if (!sidebarOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggleSidebar(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sidebarOpen, toggleSidebar]);

  return (
    <>
      {sidebarOpen && (
        <div
          className="sidebar-scrim"
          aria-hidden
          onClick={() => toggleSidebar(false)}
        />
      )}
      <aside className={`sidebar${sidebarOpen ? ' is-open' : ''}`}>
        <div className="wordmark">
          <LogoMark size={26} className="wordmark-mark" />
          <Wordmark className="wordmark-full" height={22} />
          <button
            type="button"
            className="btn btn-ghost sidebar-close"
            onClick={() => toggleSidebar(false)}
            aria-label="Close navigation menu"
          >
            <Icon name="close" size={16} />
          </button>
        </div>

        {user?.isSuperadmin && (
          <div style={{ padding: '0 20px 8px' }}>
            <span className="badge badge-vermillion mono" style={{ fontSize: 10 }}>
              SUPERADMIN
            </span>
          </div>
        )}

        <nav ref={navRef} data-tour="nav" style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
          <div
            className="nav-highlighter"
            aria-hidden
            style={{
              transform: `translateY(${hl.top}px)`,
              height: hl.height,
              opacity: hl.visible ? 1 : 0,
            }}
          />
          {navGroups.map((g) => {
            return (
              <div key={g.title} className="nav-section">
                <div className="nav-group-title">{g.title}</div>
                {g.items.map((item) => {
                  // Exact match OR /-bounded prefix (so /app/review doesn't
                  // claim /app/review-queue); tabbed items stay active on
                  // every one of their tabs.
                  const path = location.pathname;
                  const active = item.tabs
                    ? item.tabs.some((t) => pathMatches(path, t.to))
                    : pathMatches(path, item.to);
                  return (
                    <NavLink
                      key={item.id}
                      to={item.to}
                      className={`nav-item${active ? ' active' : ''}`}
                      end={false}
                    >
                      <Icon name={item.icon} className="nav-icon" />
                      <span className="nav-label" style={{ flex: 1 }}>
                        {item.label}
                      </span>
                      {item.id === 'team-chat' && chatUnread > 0 && (
                        <span
                          className="badge nav-label"
                          aria-label={`${chatUnread} unread messages`}
                          // Monochrome solid pill (red is reserved for mandatory issues).
                          style={{ fontSize: 10, padding: '0 6px', background: 'var(--text-primary)', color: 'var(--bg-base)', borderColor: 'var(--text-primary)', fontWeight: 600 }}
                        >
                          {chatUnread > 99 ? '99+' : chatUnread}
                        </span>
                      )}
                      {item.badge && (
                        <span
                          className="mono nav-label"
                          style={{ fontSize: 9, color: 'var(--text-tertiary)', letterSpacing: '0.1em' }}
                        >
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          <div className="quota">
            <div className="quota-head">
              <span className="quota-label">AI documents</span>
              <span className="quota-count">
                {usage.isLoading || !usage.data
                  ? '-'
                  : usage.data.aiDocuments.limit == null
                    ? `${usage.data.aiDocuments.used} · Unlimited`
                    : `${usage.data.aiDocuments.used} / ${usage.data.aiDocuments.limit}`}
              </span>
            </div>
            {usage.data && usage.data.aiDocuments.limit != null && (
              <div className="quota-track">
                <div
                  className="quota-fill"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round((usage.data.aiDocuments.used / usage.data.aiDocuments.limit) * 100),
                    )}%`,
                  }}
                />
              </div>
            )}
            <div className="quota-note">
              {usage.data?.aiDocuments.limit == null && usage.data
                ? 'Generated this month'
                : usage.data
                  ? `${Math.max(0, (usage.data.aiDocuments.limit ?? 0) - usage.data.aiDocuments.used)} left this month`
                  : 'Generated this month'}
            </div>
          </div>
          <AccountMenu
            name={user?.name || 'Advocate'}
            detail={[user?.role, user?.plan && `${user.plan} plan`].filter(Boolean).join(' · ')}
            initials={initials}
          />
        </div>
      </aside>
    </>
  );
}
